import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import ProductListItemVertical from "components/organisms/ProductListItemVertical";
import ProductListItemHorizontal from "components/organisms/ProductListItemHorizontal";
import { ProductListModel } from "models/productListModel";
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from "@tanstack/react-query";
import { ModelList } from "types/Common";
import { After10ApiError } from "util/request/types";
import BenefitSectionTitle from "./BenefitSectionTitle";
import CountdownTimer from "./CountdownTimer";
import { ProductOrientation } from "types/Benefit";
import { usePostCoupangViewLog } from "api/queries";
import { isAndroid } from "react-device-detect";
import { logEvent } from "firebase/analytics";
import { analytics } from "lib/firebase";
import ProductListEmpty from "./ProductListEmpty";

interface Props {
  productData: ProductListModel[];
  title?: string;
  subTitle?: string;
  setActiveTab?: (tab: number) => void;
  hasNextPage?: boolean;
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<ModelList<any>, unknown>,
      After10ApiError
    >
  >;
  isTimer?: boolean;
  isMain?: boolean;
  onTimerReached?: () => void; // timer 시간 도달 시 함수
  orientation?: ProductOrientation; // 상품 UI 형태
  isLoaded?: boolean;
}

const ProductList = ({
  productData,
  title,
  subTitle,
  setActiveTab,
  hasNextPage,
  fetchNextPage,
  isTimer,
  isMain,
  onTimerReached,
  orientation = "vertical",
  isLoaded,
}: Props) => {
  const HEADER_HEIGHT = 100;
  const categoryCd =
    (productData && productData.length > 0 && productData[0].categoryCd) || 0;
  const [timerFloating, setTimerFloating] = useState(false);
  const lastItemRef = useRef<HTMLLIElement | null>(null);
  const timerRef = useRef<HTMLDivElement | null>(null);
  const osType = isAndroid ? "1" : "2";

  const viewLogmutation = usePostCoupangViewLog();

  useEffect(() => {
    if (isMain) return;
    const checkHeight = () => {
      if (!timerRef.current) return;

      const rect = timerRef.current.getBoundingClientRect();
      const isOccludedByHeader = rect.bottom < HEADER_HEIGHT;

      setTimerFloating(isOccludedByHeader);
    };

    window.addEventListener("scroll", checkHeight);

    return () => {
      window.removeEventListener("scroll", checkHeight);
    };
  }, [isMain]);

  useEffect(() => {
    if (!lastItemRef.current || !hasNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        fetchNextPage && fetchNextPage();
      }
    });

    observer.observe(lastItemRef.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, productData]);

  const logCoupangView = ({
    productId,
    itemId,
    refTy,
    refId,
    url,
  }: {
    refTy: number;
    refId?: number;
    productId?: number;
    itemId?: number;
    url?: string;
  }) => {
    viewLogmutation.mutate({
      productId,
      itemId,
      categoryCd,
      osType,
      refTy,
      ...(refTy == 11 && { refId }),
      url,
    });
  };

  if (isLoaded && productData.length === 0) {
    return <ProductListEmpty />;
  }

  return (
    <>
      {title && (
        <BenefitSectionTitle
          title={title}
          text={subTitle || ""}
          onClick={() => {
            if (isMain && isTimer) {
              logEvent(analytics, "Web_benefit_home_1day_B");
            } else {
              logEvent(analytics, "Web_benefit_home_category_B");
            }
            setActiveTab && setActiveTab(categoryCd);
          }}
        />
      )}
      {isTimer && (
        <StyledTimerBox ref={timerRef}>
          <CountdownTimer
            onReached={onTimerReached}
            isFloating={timerFloating}
          />
        </StyledTimerBox>
      )}
      {orientation === "vertical" ? (
        <StyledProductListVertical>
          {productData.map((product, index) => {
            const isLast = index === productData.length - 1;
            return (
              <ProductListItemVertical
                key={`${product.productNm}` + index}
                ref={isLast ? lastItemRef : undefined}
                productData={product}
                onClickProduct={() => {
                  if (product.refTy) {
                    logCoupangView({
                      refTy: product.refTy,
                      refId: product.refId,
                      productId: product.productId,
                      itemId: product.itemId,
                      url: product.productUrl,
                    });
                  }
                }}
              />
            );
          })}
        </StyledProductListVertical>
      ) : (
        <StyledProductListHorizontal>
          {productData.map((product, index) => {
            const isLast = index === productData.length - 1;
            return (
              <ProductListItemHorizontal
                key={`${product.productNm}` + index}
                ref={isLast ? lastItemRef : undefined}
                productData={product}
                onClickProduct={() => {
                  if (product.refTy) {
                    logCoupangView({
                      refTy: product.refTy,
                      productId: product.productId,
                      itemId: product.itemId,
                      url: product.productUrl,
                    });
                  }
                }}
              />
            );
          })}
        </StyledProductListHorizontal>
      )}
    </>
  );
};

export default ProductList;

const StyledProductListVertical = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 16px;
  grid-auto-rows: minmax(196px, auto);
  padding: 0 20px;
`;

const StyledProductListHorizontal = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0px 20px;
`;

const StyledTimerBox = styled.div`
  margin: 0px 20px;
`;
